import { useMutation, useQuery, useQueryClient, UseQueryOptions } from "react-query";
import { api } from "../api";
import { OrganisationInvite } from "../../types/OrganisationInvite";
import { IUserOrganisation } from "../../types/IUserOrganisation";
import { User } from "../../types/User";
import { useToast } from "@chakra-ui/react";
import { useAuth } from "lib/auth/useAuth";
import {
  applySearchFallback,
  applySortFallback,
  Page,
  PageParams,
  pageQueryParams,
  unwrapListResponse,
} from "../pagination";

export enum UserQueryKeys {
  USER = "user",
  USER_INVITE = "user-invite",
  USER_INVITE_LIST = "user-invite-list",
  USER_ORGANISATION = "user-organisation",
  ORGANISATION_MEMBER_PAGE = "organisation-member-page",
}

/** Sort fields the member list accepts. They are USER columns — see the API's list. */
export const ORGANISATION_MEMBER_SORT_FIELDS = ["email", "firstName", "lastName", "userCreatedAt"] as const;

/** A membership with its user attached, as `?includeUser=true` returns it. */
export interface OrganisationMember extends IUserOrganisation {
  /** Absent when the membership outlives the user row. */
  user?: User;
}

/**
 * ONE page of an organisation's members, with the total.
 *
 * The Members page used to load every user in the organisation, every membership and every
 * pending invite, then join, search and sort them in the browser — downloading the whole
 * directory to render fifty rows, with no way to say how many members there are without
 * holding all of them.
 *
 * `includeUser` is sent even though the PAGED path always attaches the user, because the
 * unpaged path does not: while ORG_SCALE_PAGINATION is off the server ignores the paging
 * parameters and answers with the full membership list, and without this that list carries
 * no users at all — every row would render as a blank name and a blank email rather than
 * as a slow table.
 */
export const useGetOrganisationMembersPage = (
  currentOrganisationId: string | undefined,
  params: PageParams,
) => {
  const { data, ...rest } = useQuery<OrganisationMember[] | Page<OrganisationMember>>(
    [UserQueryKeys.ORGANISATION_MEMBER_PAGE, currentOrganisationId, params],
    () =>
      api.get("v2/userorganisation", {
        // Non-null: the query is disabled until there is one.
        organisationId: currentOrganisationId as string,
        includeUser: true,
        ...pageQueryParams(params),
      }),
    {
      enabled: !!currentOrganisationId,
      // The rows stay while the next page loads, so paging dims the table instead of
      // emptying it. Callers must read `isFetching` — `isLoading` is false throughout.
      keepPreviousData: true,
      // The key carries the whole `params` object, so every page, sort and settled
      // keystroke is a distinct cache entry — and while ORG_SCALE_PAGINATION is off each
      // of those entries is the ENTIRE membership list, because the server ignores the
      // paging. Without a staleTime, react-query v3 also refetches on mount and on window
      // focus, so clicking through ten pages of a 2,000-member organisation downloaded the
      // directory ten times. A minute is long enough that a page walk shares one download
      // and short enough that a removal or a role change (both of which invalidate this
      // key explicitly) still shows up immediately.
      staleTime: 60_000,
    },
  );

  const result = unwrapListResponse<OrganisationMember>(data);

  // The server ignores `search` AND `sort` while the flag is off, so both the box and the
  // column headers would be dead controls that look alive: type a surname and the page
  // shows the organisation's first fifty members unchanged; click a header and the arrow
  // moves while the rows do not. Both are applied here instead, over what really is the
  // whole list. The cap is raised for this caller because it PAGES the array itself — see
  // MEMBER_FALLBACK_LIMIT.
  const searched = applySearchFallback(
    result,
    params.search,
    (member) => [member.user?.id, member.user?.email, member.user?.firstName, member.user?.lastName],
    MEMBER_FALLBACK_LIMIT,
  );

  return {
    ...rest,
    ...applySortFallback(
      searched,
      params.sort,
      params.desc,
      getOrganisationMemberSortValue,
      (member) => member.userId,
    ),
  };
};

/**
 * How many matching members the flag-off path will hold.
 *
 * The shared default is the server's page ceiling (200), which is right for a caller
 * treating the result as ONE server page. This caller does not: it slices the array
 * itself, so a cap below the match count meant pages past the cap rendered empty. The
 * response really is the whole membership list, so holding it is what the un-paged page
 * already did — this is a ceiling against a pathological organisation, not a window.
 */
const MEMBER_FALLBACK_LIMIT = 5000;

/** How a member row is ordered by each of {@link ORGANISATION_MEMBER_SORT_FIELDS}. */
function getOrganisationMemberSortValue(member: OrganisationMember, field: string): string | undefined {
  switch (field) {
    case "email":
      return member.user?.email;
    case "firstName":
      return member.user?.firstName;
    case "lastName":
      return member.user?.lastName;
    case "userCreatedAt":
      return member.user?.createdAt;
    default:
      // `undefined` is "this row has no value for that field" and sorts first. An UNKNOWN
      // field is a different answer — nobody taught this caller how to order by it — and
      // silently returning the rows in arbitrary order under a sort indicator is the
      // failure this whole mechanism exists to prevent.
      throw new Error(
        `no fallback ordering for member sort field "${field}"; known fields: ` +
          ORGANISATION_MEMBER_SORT_FIELDS.join(", "),
      );
  }
}

export const useGetUsers = (currentOrganisationId: string) => {
  const { data: users, ...rest } = useQuery<User[]>(
    [UserQueryKeys.USER, currentOrganisationId],
    () => api.get(`v2/user?organisationId=${currentOrganisationId}`),
    { enabled: !!currentOrganisationId, staleTime: Infinity },
  );

  return { users, ...rest };
};

export const useGetUser = (userId: string | undefined) => {
  const { data: user, ...rest } = useQuery<User>(
    [UserQueryKeys.USER, userId],
    () => api.get(`v2/user/${userId}`),
    {
      staleTime: Infinity,
      enabled: !!userId,
    },
  );

  return { user, ...rest };
};

export const useGetUserInvites = (currentOrganisationId: string) => {
  const { data: userInvites, ...rest } = useQuery<OrganisationInvite[]>(
    [UserQueryKeys.USER_INVITE_LIST, currentOrganisationId],
    () => api.get(`account/v2/invite?organisationId=${currentOrganisationId}`),
    {
      // stale time 1 min
      staleTime: 6000,
    },
  );
  return { userInvites, ...rest };
};

export const useGetUserOrganisationsForUser = (userId: string | undefined) => {
  const { data: userOrganisations, ...rest } = useQuery<IUserOrganisation[]>(
    [UserQueryKeys.USER_ORGANISATION, userId],
    () => api.get(`v2/userorganisation?userId=${userId}`),
    {
      staleTime: Infinity,
    },
  );
  return { userOrganisations, ...rest };
};

export const useRemoveUserMutation = () => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { user: currentUser } = useAuth();

  const apiRemoveUser = async (userId: string) => {
    if (!currentUser) return;
    if (userId === currentUser.id) {
      toast({
        title: "Unable to remove user",
        description: "You cannot remove yourself as a user",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    await api.del(`v2/userorganisation?organisationId=${currentUser.currentOrganisationId}&userId=${userId}`);
  };

  return useMutation(apiRemoveUser, {
    onSuccess: () => {
      queryClient.invalidateQueries(UserQueryKeys.USER);
      // The Members table reads ORGANISATION_MEMBER_PAGE now, which nothing else
      // invalidates — without this the removed member's row stayed on screen with its
      // Remove and Edit actions still live, and the footer total unchanged, until a hard
      // reload. It worked before this branch because the table read USER directly.
      queryClient.invalidateQueries(UserQueryKeys.ORGANISATION_MEMBER_PAGE);
    },
    onError: (error: Error) => {
      toast({
        title: "Unable to remove user",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    },
  });
};

export const useUpdateUserOrganisationMutation = () => {
  const queryClient = useQueryClient();
  const toast = useToast();

  const apiUpdateUserOrganisation = async ({
    userId,
    organisationId,
    userRoleId,
  }: {
    userId: string;
    organisationId: string;
    userRoleId?: number;
  }) => {
    await api.post(`/v2/userorganisation`, {
      userId,
      organisationId,
      userRoleId: Number(userRoleId),
    });
  };

  return useMutation(apiUpdateUserOrganisation, {
    onSuccess: () => {
      queryClient.invalidateQueries(UserQueryKeys.USER_ORGANISATION);
      // Same reason as the removal above: the Role column is rendered from the members
      // page query, so without this it kept showing the old role after an edit.
      queryClient.invalidateQueries(UserQueryKeys.ORGANISATION_MEMBER_PAGE);
    },
    onError: (error: Error) => {
      toast({
        title: "Unable to update user",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    },
  });
};
